import React, { useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { Button, IconButton } from "react-native-paper";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useNavigation } from "@react-navigation/native";
import { auth } from "../firebase";
import {
  addParticipantToRoom,
  getScheduleConflictError,
} from "../services/roomsService";
import type { Room } from "../types/types";
import type RootStackParamList from "../navigation/RootStackParamList";

type Props = NativeStackScreenProps<RootStackParamList, "JoinConfirm">;

export default function JoinRoomConfirmScreen({ route }: Props) {
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const room: Room = route.params.room;

  const [isProcessing, setIsProcessing] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const userId = auth.currentUser?.uid || "";

  // Check if this is user's own room
  const isOwnRoom = room.createdBy === userId;
  const isJoinDisabled = isProcessing || !userId || isOwnRoom;
  const confirmButtonColor = isOwnRoom ? "#9CA3AF" : "#3A6FB1";

  const handleClose = () => {
    navigation.goBack();
  };

  const handleConfirm = async () => {
    if (!room.id) return;
    if (!userId) {
      console.warn("You must be signed in to join the meeting.");
      return;
    }

    // Prevent joining own room
    if (isOwnRoom) {
      console.warn("Cannot join own room");
      return;
    }

    try {
      setIsProcessing(true);
      setJoinError(null);
      // One person can't attend two conversations at once (#141).
      const conflictError = await getScheduleConflictError(
        userId,
        room.startAt,
        room.id,
      );
      if (conflictError) {
        setJoinError(conflictError);
        return;
      }
      await addParticipantToRoom(room.id, userId);
      handleClose();
    } catch (error) {
      console.error("Failed to join the room:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <View style={styles.root}>
      <Pressable style={styles.backdrop} onPress={handleClose}>
        {/* Centered card */}
        <View style={styles.centered} pointerEvents="box-none">
          <Pressable
            style={[styles.card, { width: Math.min(width - 32, 469) }]}
            testID="join-confirm-card"
            onPress={() => {}}
          >
            <View style={styles.textContainer}>
              <View style={styles.header}>
                <Text style={styles.title}>Join The Conversation?</Text>
              </View>
              <IconButton
                icon="close"
                size={20}
                onPress={handleClose}
                accessibilityLabel="Close"
                testID="join-confirm-close"
                style={styles.closeButton}
              />

              <Text style={styles.subtitle}>
                {room?.name ? `Room: ${room.name}` : "Room: Unnamed room"}
              </Text>
              {(() => {
                const durationMin =
                  room?.startAt && room?.endAt
                    ? Math.round(
                        (room.endAt.getTime() - room.startAt.getTime()) / 60000,
                      )
                    : 20;
                const roomMaxCapacity = room?.maxParticipants ?? 3;
                return (
                  <Text style={styles.metaText}>
                    {durationMin} minutes · {roomMaxCapacity} people
                  </Text>
                );
              })()}
              {isOwnRoom && (
                <Text style={styles.warningText}>
                  You cannot join a room you created.
                </Text>
              )}
              {joinError && (
                <Text style={styles.warningText} testID="join-confirm-error">
                  {joinError}
                </Text>
              )}
            </View>

            <View style={styles.divider} />

            <View style={styles.actions}>
              <Button
                mode="contained"
                onPress={handleConfirm}
                disabled={isJoinDisabled}
                buttonColor={confirmButtonColor}
                textColor="#fff"
                style={[
                  styles.confirmButton,
                  isOwnRoom && styles.confirmButtonDisabled,
                ]}
                contentStyle={styles.confirmButtonContent}
                labelStyle={styles.confirmButtonLabel}
              >
                {isProcessing ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  "YES, JOIN NOW"
                )}
              </Button>
            </View>
          </Pressable>
        </View>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "transparent",
  },
  backdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
  },
  card: {
    backgroundColor: "white",
    borderRadius: 16,
    maxWidth: 469,
    minHeight: 283,
    paddingTop: 16,
    paddingRight: 16,
    paddingBottom: 32,
    paddingLeft: 16,
    borderWidth: 1,
    borderColor: "#d8dbe1",
    justifyContent: "space-between",
    elevation: 6,
    position: "relative",
  },
  textContainer: {
    alignItems: "flex-start",
  },
  divider: {
    width: "100%",
    height: 3,
    borderRadius: 100,
    backgroundColor: "#D8DBE1",
  },
  header: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    marginBottom: 24,
  },
  title: {
    fontSize: 26,
    fontWeight: "700",
    lineHeight: 28,
    fontFamily: "Lexend",
    color: "#2b2b2f",
    textAlign: "left",
    flex: 1,
  },
  closeButton: {
    position: "absolute",
    top: 6,
    right: 6,
  },
  subtitle: {
    fontSize: 16,
    fontWeight: "700",
    lineHeight: 14,
    fontFamily: "Lexend",
    color: "#6E6E6E",
    alignSelf: "flex-start",
    marginBottom: 4,
  },
  metaText: {
    fontSize: 14,
    lineHeight: 18,
    color: "#6E6E6E",
  },
  warningText: {
    marginTop: 8,
    fontSize: 12,
    lineHeight: 16,
    color: "#b91c1c",
    fontWeight: "600",
  },
  infoBox: {
    backgroundColor: "#F1F5F9",
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
  },
  infoText: { color: "#334155", fontSize: 13 },
  actions: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 12,
    width: "100%",
  },
  confirmButton: {
    width: "100%",
    height: 62,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#3A6FB1",
    backgroundColor: "#3A6FB1",
  },
  confirmButtonDisabled: {
    borderColor: "#9CA3AF",
    backgroundColor: "#9CA3AF",
  },
  confirmButtonContent: {
    height: 62,
    paddingHorizontal: 20,
  },
  confirmButtonLabel: {
    fontSize: 14,
    fontWeight: "700",
    letterSpacing: 0.6,
    color: "#F6F7F9",
  },
});
